/**
 * Ingredient Glossary Web Component
 * Handles alphabet filtering, search functionality, and dynamic JSON loading
 */

if (!customElements.get('ingredient-glossary')) {
  class IngredientGlossary extends HTMLElement {
    constructor() {
      super();
      
      this.grid = this.querySelector('.ingredient-glossary__grid');
      this.letterButtons = this.querySelectorAll('.ingredient-glossary__letter');
      this.noResults = this.querySelector('.ingredient-glossary__no-results');
      this.searchToggle = this.querySelector('.ingredient-glossary__search-toggle');
      this.searchWrapper = this.querySelector('.ingredient-glossary__search-input-wrapper');
      this.searchInput = this.querySelector('.ingredient-glossary__search-input');
      this.searchClose = this.querySelector('.ingredient-glossary__search-close');
      
      this.currentLetter = 'A';
      this.isSearchMode = false;
      this.ingredients = [];
      this.items = [];
      
      this.init();
    }

    async init() {
      await this.loadIngredients();
      this.items = this.querySelectorAll('.ingredient-glossary__item');
      this.bindEvents();
      this.updateDisabledLetters();
      this.filterByLetter('A');
    }

    async loadIngredients() {
      const url = this.grid.dataset.ingredientsUrl;
      if (!url) return;

      try {
        const response = await fetch(url);
        const data = await response.json();
        this.ingredients = data.ingredients || [];
        this.renderIngredients();
      } catch (error) {
        console.error('Error loading ingredients:', error);
      }
    }

    renderIngredients() {
      if (!this.grid || !this.ingredients.length) return;

      const html = this.ingredients.map(ingredient => `
        <article 
          class="ingredient-glossary__item"
          data-letter="${ingredient.category}"
          data-title="${ingredient.title.toLowerCase()}"
        >
          <h3 class="ingredient-glossary__item-title">${ingredient.title}</h3>
          <div class="ingredient-glossary__item-content">
            ${ingredient.content}
          </div>
          ${ingredient.included_in ? `
            <div class="ingredient-glossary__item-included">
              <span class="ingredient-glossary__item-label">INCLUDED IN:</span>
              ${ingredient.link 
                ? `<a href="${ingredient.link}" class="ingredient-glossary__item-link">${ingredient.included_in}</a>`
                : `<span class="ingredient-glossary__item-link">${ingredient.included_in}</span>`
              }
            </div>
          ` : ''}
        </article>
      `).join('');

      this.grid.innerHTML = html;
    }

    bindEvents() {
      // Letter buttons
      this.letterButtons.forEach(button => {
        button.addEventListener('click', this.handleLetterClick.bind(this));
      });

      // Search functionality
      if (this.searchToggle) {
        this.searchToggle.addEventListener('click', this.toggleSearch.bind(this));
      }

      if (this.searchClose) {
        this.searchClose.addEventListener('click', this.closeSearch.bind(this));
      }

      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.handleSearch.bind(this));
        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.closeSearch();
          }
        });
      }

      // Close search on click outside
      document.addEventListener('click', (e) => {
        if (this.isSearchMode && !this.contains(e.target)) {
          this.closeSearch();
        }
      });
    }

    handleLetterClick(event) {
      const button = event.currentTarget;
      const letter = button.dataset.letter;

      if (button.classList.contains('is-disabled')) {
        return;
      }

      // Exit search mode if active
      if (this.isSearchMode) {
        this.closeSearch();
      }

      this.setActiveLetter(button);
      this.filterByLetter(letter);
    }

    setActiveLetter(activeButton) {
      this.letterButtons.forEach(btn => btn.classList.remove('is-active'));
      activeButton.classList.add('is-active');
      this.currentLetter = activeButton.dataset.letter;
    }

    filterByLetter(letter) {
      let visibleCount = 0;

      this.items.forEach(item => {
        const itemLetter = item.dataset.letter;
        
        if (itemLetter === letter) {
          item.hidden = false;
          visibleCount++;
        } else {
          item.hidden = true;
        }
      });

      this.toggleNoResults(visibleCount === 0);
    }

    updateDisabledLetters() {
      const availableLetters = new Set();

      this.items.forEach(item => {
        const letter = item.dataset.letter;
        if (letter) {
          availableLetters.add(letter.toUpperCase());
        }
      });

      this.letterButtons.forEach(button => {
        const letter = button.dataset.letter;
        if (!availableLetters.has(letter)) {
          button.classList.add('is-disabled');
        } else {
          button.classList.remove('is-disabled');
        }
      });
    }

    toggleSearch() {
      if (this.isSearchMode) {
        this.closeSearch();
      } else {
        this.openSearch();
      }
    }

    openSearch() {
      this.isSearchMode = true;
      this.searchWrapper.hidden = false;
      this.searchToggle.hidden = true;
      this.searchInput.focus();

      // Clear letter selection
      this.letterButtons.forEach(btn => btn.classList.remove('is-active'));

      // Show all items initially
      this.items.forEach(item => {
        item.hidden = false;
      });
      this.toggleNoResults(false);
    }

    closeSearch() {
      this.isSearchMode = false;
      this.searchWrapper.hidden = true;
      this.searchToggle.hidden = false;
      this.searchInput.value = '';

      // Restore letter filter
      const activeButton = Array.from(this.letterButtons).find(
        btn => btn.dataset.letter === this.currentLetter
      );
      
      if (activeButton) {
        activeButton.classList.add('is-active');
        this.filterByLetter(this.currentLetter);
      }
    }

    handleSearch(event) {
      const query = event.target.value.toLowerCase().trim();

      if (query === '') {
        this.items.forEach(item => {
          item.hidden = false;
        });
        this.toggleNoResults(false);
        return;
      }

      let visibleCount = 0;

      this.items.forEach(item => {
        const title = item.dataset.title || '';
        const content = item.textContent.toLowerCase();

        if (title.includes(query) || content.includes(query)) {
          item.hidden = false;
          visibleCount++;
        } else {
          item.hidden = true;
        }
      });

      this.toggleNoResults(visibleCount === 0);
    }

    toggleNoResults(show) {
      if (this.noResults) {
        this.noResults.hidden = !show;
      }
    }
  }

  customElements.define('ingredient-glossary', IngredientGlossary);
}
